import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';

const StatCard = ({ icon, label, value, sub, color = 'var(--accent)' }) => (
  <div className="card" style={{ padding:18, display:'flex', alignItems:'flex-start', gap:14 }}>
    <div style={{ width:42, height:42, borderRadius:10, background:`${color}22`, color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{icon}</div>
    <div>
      <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, color:'var(--text-primary)', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--text-secondary)', marginTop:4 }}>{sub}</div>}
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/admin/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-wrap"><div className="spinner"></div></div>;
  if (!data) return <div className="empty-state"><p>Could not load dashboard.</p></div>;

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Admin Dashboard</h1><p className="page-sub">Fit to Fit Gym — overview</p></div>
        <Link to="/admin/members" className="btn-primary">+ Add member</Link>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:14, marginBottom:22 }}>
        <StatCard icon="👥" label="Total members"   value={data.total_members}    sub="Registered accounts"  color="var(--accent)" />
        <StatCard icon="✅" label="Active members"  value={data.active_members}   sub="Valid memberships"    color="var(--success)" />
        <StatCard icon="⚠️" label="Expiring soon"   value={data.expired_soon}     sub="Within 7 days"        color="var(--warning)" />
        <StatCard icon="📅" label="Today's check-ins" value={data.today_attendance} sub="Gym visits today"  color="#a855f7" />
        <StatCard icon="💰" label="Revenue this month" value={`₨${Number(data.revenue_month).toLocaleString()}`} sub="Cash collected"  color="var(--success)" />
        <StatCard icon="🏆" label="Total revenue"   value={`₨${Number(data.revenue_total).toLocaleString()}`} sub="All time"  color="var(--accent)" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Revenue chart */}
        <div className="card">
          <div className="card-header"><div className="card-title">📈 Monthly revenue (₨)</div></div>
          <div style={{ padding:'16px 8px' }}>
            {data.monthly_revenue?.length ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.monthly_revenue} margin={{ left:0, right:0 }}>
                  <XAxis dataKey="month" tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill:'var(--text-muted)', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => `₨${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`₨${Number(v).toLocaleString()}`, 'Revenue']} contentStyle={{ background:'var(--surface-2)', border:'1px solid var(--border)', borderRadius:8, fontSize:12 }} />
                  <Bar dataKey="revenue" fill="var(--accent)" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>No revenue data yet.</p></div>}
          </div>
        </div>

        {/* Recent members */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🆕 Recent members</div>
            <Link to="/admin/members" className="btn-ghost" style={{ fontSize:12, padding:'5px 10px' }}>View all</Link>
          </div>
          <div>
            {data.recent_members?.map(m => (
              <Link to={`/admin/members/${m.id}`} key={m.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 18px', borderBottom:'1px solid var(--border)', textDecoration:'none', transition:'.12s' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <div className="avatar">
                  {m.photo ? <img src={m.photo} alt={m.name} /> : m.name.slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)' }}>{m.name}</div>
                  <div style={{ fontSize:11, color:'var(--text-muted)' }}>{m.plan_name || 'No plan'}</div>
                </div>
                <span className={`badge ${m.membership_status === 'active' ? 'badge-success' : 'badge-muted'}`}>
                  {m.membership_status || 'pending'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
